import { z } from 'zod';
/**
 * Footstep trails (ADR 0073): a cosmetic worn in the `look-trail` place that
 * leaves a wake of tiny sprites behind its wearer. Nothing of it is drawn on
 * the body, so its template has no overlay: the client scatters the looping
 * effect `sprite` (`effects/trail-<id>/<i>.png`, drawn pixel by pixel by
 * `scripts/draw-trail-particles.py`) from the numbers below.
 *
 * - `rise`: it climbs from the feet and sways (embers).
 * - `fall`: it tumbles down from the waist and lies on the ground (petals).
 * - `settle`: it stays where it was left and twinkles (frost).
 * - `drift`: it tumbles along the ground (dead leaves).
 */
export const TRAIL_MOTIONS = ['rise', 'fall', 'settle', 'drift'];
const TRAIL_ID = /^[a-z][a-z0-9-]*$/;
export const TrailSchema = z.object({
    id: z.string().regex(TRAIL_ID),
    motion: z.enum(TRAIL_MOTIONS),
    /** Effect sheet in the manifest: `variants` looks of `frames` images, one after the other. */
    sprite: z.string().regex(/^effect\.trail-[a-z0-9-]+$/),
    variants: z.number().int().min(1).max(4),
    frames: z.number().int().min(1).max(8),
    /** How long each image of a look shows: the tumble, the flicker, the twinkle. */
    frameMs: z.number().int().min(40).max(1000),
    /** Pixels walked between two puffs: a tile is 16. */
    every: z.number().int().min(2).max(24),
    /** Sprites a puff leaves. */
    count: z.number().int().min(1).max(3),
    /** How long a sprite lasts, fade included. */
    lifeMs: z.number().int().min(200).max(3000),
    /** How far from the feet a sprite may start, sideways. */
    spread: z.number().int().min(0).max(8),
    /** How far it travels over its life: up for `rise`, down for `fall`, along for `drift`. */
    reach: z.number().int().min(0).max(24),
});
const DEFINITIONS = [
    {
        id: 'embers',
        motion: 'rise',
        sprite: 'effect.trail-embers',
        variants: 1,
        frames: 4,
        frameMs: 90,
        every: 4,
        count: 2,
        lifeMs: 1100,
        spread: 5,
        reach: 18,
    },
    {
        id: 'frost',
        motion: 'settle',
        sprite: 'effect.trail-frost',
        variants: 1,
        frames: 4,
        frameMs: 160,
        every: 4,
        count: 2,
        lifeMs: 1800,
        spread: 6,
        reach: 0,
    },
    {
        id: 'petals',
        motion: 'fall',
        sprite: 'effect.trail-petals',
        variants: 2,
        frames: 4,
        frameMs: 120,
        every: 5,
        count: 2,
        lifeMs: 2200,
        spread: 8,
        reach: 18,
    },
    {
        id: 'leaves',
        motion: 'drift',
        sprite: 'effect.trail-leaves',
        variants: 2,
        frames: 4,
        frameMs: 110,
        every: 5,
        count: 2,
        lifeMs: 1900,
        spread: 6,
        reach: 14,
    },
    // The shop packs' own trails (`cosmetics/pack-trails.ts`).
    {
        id: 'stardust',
        motion: 'rise',
        sprite: 'effect.trail-stardust',
        variants: 2,
        frames: 4,
        frameMs: 110,
        every: 4,
        count: 2,
        lifeMs: 1300,
        spread: 5,
        reach: 16,
    },
    {
        id: 'ashes',
        motion: 'rise',
        sprite: 'effect.trail-ashes',
        variants: 2,
        frames: 4,
        frameMs: 120,
        every: 5,
        count: 2,
        lifeMs: 1600,
        spread: 6,
        reach: 14,
    },
    {
        id: 'straw',
        motion: 'drift',
        sprite: 'effect.trail-straw',
        variants: 2,
        frames: 4,
        frameMs: 110,
        every: 5,
        count: 2,
        lifeMs: 1700,
        spread: 6,
        reach: 12,
    },
    {
        id: 'fireflies',
        motion: 'rise',
        sprite: 'effect.trail-fireflies',
        variants: 1,
        frames: 4,
        frameMs: 150,
        every: 6,
        count: 2,
        lifeMs: 1800,
        spread: 7,
        reach: 12,
    },
    {
        id: 'spores',
        motion: 'rise',
        sprite: 'effect.trail-spores',
        variants: 1,
        frames: 4,
        frameMs: 140,
        every: 5,
        count: 2,
        lifeMs: 1500,
        spread: 6,
        reach: 10,
    },
    {
        id: 'sand',
        motion: 'drift',
        sprite: 'effect.trail-sand',
        variants: 1,
        frames: 4,
        frameMs: 90,
        every: 4,
        count: 2,
        lifeMs: 1200,
        spread: 6,
        reach: 12,
    },
    {
        id: 'snow',
        motion: 'fall',
        sprite: 'effect.trail-snow',
        variants: 2,
        frames: 4,
        frameMs: 140,
        every: 5,
        count: 2,
        lifeMs: 2000,
        spread: 8,
        reach: 16,
    },
];
export const TRAILS = Object.freeze(Object.fromEntries(DEFINITIONS.map((raw) => {
    const trail = TrailSchema.parse(raw);
    return [trail.id, trail];
})));
export function getTrail(id) {
    return TRAILS[id];
}
//# sourceMappingURL=trails.js.map